import { type Viewer, type LockHolder } from "../realtime/RealtimeProvider";
import { cn } from "../lib/cn";

/**
 * Stacked initials for everyone viewing the current diagram.
 *
 * The lock holder is ringed in the accent colour; everyone else is neutral, so
 * "who is actually editing" reads at a glance rather than needing the tooltip.
 */
export function PresenceAvatars({
  viewers,
  lock,
  selfId,
  max = 4,
}: {
  viewers: Viewer[];
  lock: LockHolder | null;
  selfId: string | null;
  max?: number;
}) {
  // Nothing useful to show when you are the only one here.
  if (viewers.length <= 1) return null;

  const shown = viewers.slice(0, max);
  const overflow = viewers.length - shown.length;

  return (
    <div className="flex items-center" aria-label={`${viewers.length} people viewing`}>
      {shown.map((viewer, index) => {
        const isSelf = selfId !== null && String(viewer.userId) === selfId;
        const isEditor = lock !== null && String(lock.userId) === String(viewer.userId);

        return (
          <span
            key={viewer.userId}
            title={
              isEditor
                ? `${viewer.username}${isSelf ? " (you)" : ""} — editing`
                : `${viewer.username}${isSelf ? " (you)" : ""} — viewing`
            }
            style={{ zIndex: shown.length - index }}
            className={cn(
              "relative flex h-6 w-6 items-center justify-center rounded-full",
              "text-[10px] font-semibold ring-2",
              index > 0 && "-ms-1.5",
              isEditor
                ? "bg-accent text-white ring-accent-soft"
                : "bg-surface-sunken text-ink-muted ring-surface",
            )}
          >
            {viewer.username.slice(0, 2).toUpperCase()}
          </span>
        );
      })}

      {overflow > 0 && (
        <span
          title={viewers
            .slice(max)
            .map((v) => v.username)
            .join(", ")}
          className="bg-surface-sunken text-ink-muted ring-surface relative -ms-1.5 flex h-6 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ring-2"
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

export default PresenceAvatars;
